import {
  Alert,
  Badge,
  Box,
  Button,
  Flex,
  Group,
  Input,
  NumberInput,
  Progress,
  rem,
  SegmentedControl,
  Select,
  Skeleton,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { FloppyDiskIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import { i18n, serverOptionsFilter } from "@dehub/theme";
import { yupResolver } from "mantine-form-yup-resolver";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import useSwr from "swr";
import * as yup from "yup";
import { InvoiceApi, NetworkApi } from "../../apis";
import { useDebouncedSearch } from "../../hooks/useDebouncedSearch";
import { AppModalHeader } from "../ui/app-modal";
import { IGeneral } from "../../interfaces/IGeneral";
import { IReference } from "../../interfaces/IReference";
import { currencyFormat } from "../../utils";
import { message } from "../../utils/Message";
import { renderBusinessName } from "../../utils/render-business-name";
import { MultiFileUpload } from "../upload/multi-file-upload";
import {
  AllocationEntry,
  AllocationRow,
  AllocationTable,
} from "./register-table";
import classes from "./RegisterPayment.module.css";

const cents = (v: number) => Math.round(parseFloat(`${v || 0}`) * 100);
const PAGE_SIZE = 10;

/** Shared Drawer styles so header/footer pin and only `.body` scrolls. */
export const REGISTER_PAYMENT_DRAWER_STYLES = {
  content: {
    display: "flex",
    flexDirection: "column" as const,
    overflow: "hidden",
  },
  body: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column" as const,
    overflow: "hidden",
  },
};

type FormValues = {
  receiverBusinessId: string | null;
  paymentMethod: string;
  totalAmount: number | string;
  trxRefCode: string;
  files: string[];
};

export function RegisterPayment({
  action,
  setAction,
  reload,
  prefillBusinessId,
}: {
  action: any;
  setAction: (e: any) => void;
  reload: () => void;
  /** Pre-select this counterparty when the drawer opens (customer detail). */
  prefillBusinessId?: string | null;
}) {
  const { t } = useTranslation("common");
  const { t: ti } = useTranslation("invoice");
  const paymentMethods = useSelector(
    (state: { general: IGeneral }) => state.general.paymentMethod,
  );

  const [allocations, setAllocations] = useState<Record<string, AllocationEntry>>({});
  const [search, setSearch] = useState("");
  const [dueTab, setDueTab] = useState<"all" | "overdue">("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const schema = useMemo(
    () =>
      yup.object({
        receiverBusinessId: yup.string().required(() => i18n.t("common:mustFill")),
        paymentMethod: yup.string().required(() => i18n.t("common:mustFill")),
        totalAmount: yup
          .number()
          .typeError(() => i18n.t("common:mustFill"))
          .positive(() => i18n.t("common:mustFill"))
          .required(() => i18n.t("common:mustFill")),
        trxRefCode: yup
          .string()
          .max(45)
          .required(() => i18n.t("common:mustFill")),
        files: yup
          .array()
          .of(yup.string().required())
          .min(1, () => i18n.t("common:mustFill"))
          .max(5, () => ti("regPayForm.maxFiles"))
          .required(() => i18n.t("common:mustFill")),
      }),
    [ti],
  );

  const form = useForm<FormValues>({
    initialValues: {
      receiverBusinessId: prefillBusinessId ?? null,
      paymentMethod: "BARTER",
      totalAmount: "",
      trxRefCode: "",
      files: [],
    },
    validate: yupResolver(schema),
  });

  useEffect(() => {
    if (action?.[0] === "payment_register" && prefillBusinessId) {
      form.setFieldValue("receiverBusinessId", prefillBusinessId);
    }
  }, [action, prefillBusinessId]);

  const {
    query: buyerQuery,
    setQuery: setBuyerQuery,
    data: buyerOptions,
  } = useDebouncedSearch<any>({
    key: "reg.pay.buyer.select",
    fetcher: (q) => NetworkApi.business_select({ query: q }),
    delay: 800,
  });

  const receiverBusinessId = form.values.receiverBusinessId;

  const {
    data: listData,
    isLoading: listLoading,
    error: listError,
    mutate: mutateList,
  } = useSwr(
    receiverBusinessId ? `swr.invoice.history.invoice_list.${receiverBusinessId}` : null,
    async () =>
      InvoiceApi.invoice_list({
        filter: { receiverBusinessId, order: "due" },
        offset: { page: 1, limit: 500 },
      }),
  );

  const listFailed = !!listError;

  const rows: AllocationRow[] = useMemo(() => {
    const raw = (listData?.rows as any[]) ?? [];
    return raw.map((r) => ({
      id: r.id,
      refCode: r.refCode,
      createdAt: r.createdAt,
      paymentDate: r.paymentDate,
      confirmedAmount: Number(r.confirmedAmount ?? 0),
      amountToPay: Number(r.amountToPay ?? 0),
      remainingDays: r.remainingDays == null ? null : Number(r.remainingDays),
    }));
  }, [listData]);

  const count = Number(listData?.count ?? rows.length);
  const overdueRows = useMemo(
    () => rows.filter((r) => r.remainingDays != null && r.remainingDays < 0),
    [rows],
  );

  const totalRemaining = useMemo(
    () => rows.reduce((s, r) => s + cents(r.amountToPay), 0) / 100,
    [rows],
  );
  const overdueRemaining = useMemo(
    () => overdueRows.reduce((s, r) => s + cents(r.amountToPay), 0) / 100,
    [overdueRows],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = dueTab === "overdue" ? overdueRows : rows;
    if (q) {
      list = list.filter((r) => (r.refCode ?? "").toLowerCase().includes(q));
    }
    return list;
  }, [rows, overdueRows, search, dueTab]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const totalAmountNum = Number(form.values.totalAmount) || 0;
  const allocated = useMemo(
    () => Object.values(allocations).reduce((s, a) => s + cents(a.amount), 0) / 100,
    [allocations],
  );
  const diffCents = cents(totalAmountNum) - cents(allocated);
  const diff = diffCents / 100;

  const methodData = useMemo(
    () =>
      (paymentMethods ?? [])
        .filter((m: IReference) => m.code === "BARTER" || m.code === "CASH")
        .map((m: IReference) => ({
          value: m.code,
          label: m.name,
        })),
    [paymentMethods],
  );

  const selectedCount = Object.keys(allocations).length;

  const canSubmit = useMemo(() => {
    if (listFailed) return false;
    if (!form.values.receiverBusinessId) return false;
    if (!form.values.paymentMethod) return false;
    if (!(totalAmountNum > 0)) return false;
    if (!form.values.trxRefCode.trim()) return false;
    const files = form.values.files ?? [];
    if (files.length < 1 || files.length > 5) return false;
    const entries = Object.values(allocations);
    if (entries.length < 1) return false;
    if (entries.some((a) => !(cents(a.amount) > 0))) return false;
    if (entries.some((a) => cents(a.amount) > cents(a.amountToPay))) return false;
    if (diffCents !== 0) return false;
    return true;
  }, [listFailed, form.values, allocations, totalAmountNum, diffCents]);

  const resetBuyerDependent = () => {
    setAllocations({});
    setSearch("");
    setPage(1);
    setDueTab("all");
  };

  const onToggle = (row: AllocationRow) => {
    setAllocations((prev) => {
      if (prev[row.id]) {
        const next = { ...prev };
        delete next[row.id];
        return next;
      }
      const allocatedNow = Object.values(prev).reduce((s, a) => s + cents(a.amount), 0);
      const free = totalAmountNum
        ? cents(totalAmountNum) - allocatedNow
        : null;
      const amount =
        free === null || free <= 0
          ? row.amountToPay
          : Math.min(cents(row.amountToPay), free) / 100;
      return {
        ...prev,
        [row.id]: {
          invoiceId: row.id,
          refCode: row.refCode ?? "",
          amountToPay: row.amountToPay,
          amount,
        },
      };
    });
  };

  const onAmountChange = (invoiceId: string, value: number | string) => {
    const num = value === "" || value == null ? 0 : Number(value);
    setAllocations((prev) => {
      const cur = prev[invoiceId];
      if (!cur) return prev;
      return {
        ...prev,
        [invoiceId]: { ...cur, amount: Number.isFinite(num) ? num : 0 },
      };
    });
  };

  const onAutoAllocate = () => {
    if (!totalAmountNum || rows.length === 0) return;
    let remaining = cents(totalAmountNum);
    const next: Record<string, AllocationEntry> = {};
    for (const row of rows) {
      if (remaining <= 0) break;
      const take = Math.min(cents(row.amountToPay), remaining);
      if (take <= 0) continue;
      next[row.id] = {
        invoiceId: row.id,
        refCode: row.refCode ?? "",
        amountToPay: row.amountToPay,
        amount: take / 100,
      };
      remaining -= take;
    }
    setAllocations(next);
    setDueTab("all");
    setPage(1);
  };

  const onSubmit = async () => {
    const result = form.validate();
    if (result.hasErrors) return;
    if (!canSubmit || loading) return;

    setLoading(true);
    try {
      await InvoiceApi.historyCreate({
        receiverBusinessId: form.values.receiverBusinessId,
        paymentMethod: form.values.paymentMethod,
        totalAmount: totalAmountNum,
        trxRefCode: form.values.trxRefCode.trim(),
        files: form.values.files,
        invoices: Object.values(allocations).map((a) => ({
          invoiceId: a.invoiceId,
          amount: a.amount,
        })),
      });
      message.success(ti("regPayForm.success"));
      setAction([]);
      reload();
    } catch (error: any) {
      message.error(error?.message ?? t("actionFailed"));
    } finally {
      setLoading(false);
    }
  };

  let statusColor = "dimmed";
  let statusText = ti("regPayForm.statusEnterTotal");
  if (totalAmountNum > 0) {
    if (diffCents === 0) {
      statusColor = "green.7";
      statusText = ti("regPayForm.statusFull");
    } else if (diffCents > 0) {
      statusColor = "gray.8";
      statusText = ti("regPayForm.statusUnder", {
        amount: currencyFormat(diff, { withSymbol: true }),
      });
    } else {
      statusColor = "red.7";
      statusText = ti("regPayForm.statusOver", {
        amount: currencyFormat(-diff, { withSymbol: true }),
      });
    }
  }

  const progressValue =
    totalAmountNum > 0 ? Math.min(allocated / totalAmountNum, 1) * 100 : 0;

  return (
    <Box className={classes.root}>
      <AppModalHeader
        bordered
        title={ti("regPayForm.title")}
        subtitle={ti("regPayForm.subtitle")}
        onClose={() => setAction([])}
      />

      <Box className={classes.body}>
        <Box className={classes.section}>
          <Box className={classes.sectionHead}>
            <Box className={classes.sectionTitle}>
              <Box component="span" className={classes.sectionNum}>
                1
              </Box>
              <Text fw={600} fz="sm">
                {ti("regPayForm.section1")}
              </Text>
            </Box>
          </Box>

          <Box className={classes.grid4}>
            <Select
              searchable
              clearable
              filter={serverOptionsFilter}
              label={ti("regPayForm.buyer")}
              required
              searchValue={buyerQuery}
              onSearchChange={setBuyerQuery}
              data={(buyerOptions ?? []).map((item: any) => ({
                value: item?.id ?? "",
                label: renderBusinessName(item, ""),
              }))}
              value={form.values.receiverBusinessId}
              onChange={(v) => {
                form.setFieldValue("receiverBusinessId", v);
                resetBuyerDependent();
              }}
              error={form.errors.receiverBusinessId}
              nothingFoundMessage={t("noResultsShort")}
            />
            <Select
              label={ti("regPayForm.paymentMethod")}
              required
              clearable={false}
              data={methodData}
              value={form.values.paymentMethod}
              onChange={(v) => form.setFieldValue("paymentMethod", v ?? "BARTER")}
              error={form.errors.paymentMethod}
            />
            <NumberInput
              className={classes.amountInput}
              label={ti("regPayForm.receivedAmount")}
              required
              thousandSeparator=","
              decimalScale={2}
              fixedDecimalScale
              min={0}
              hideControls
              value={form.values.totalAmount}
              onChange={(v) => form.setFieldValue("totalAmount", v)}
              error={form.errors.totalAmount}
            />
            <TextInput
              label={ti("regPayForm.trxRef")}
              required
              maxLength={45}
              {...form.getInputProps("trxRefCode")}
            />
          </Box>

          <Box className={classes.gridSide}>
            <Input.Wrapper
              label={ti("regPayForm.receipt")}
              required
              error={form.errors.files}
            >
              <MultiFileUpload
                value={form.values.files}
                onChange={(files) => {
                  if (files.length > 5) {
                    form.setFieldError("files", ti("regPayForm.maxFiles"));
                    return;
                  }
                  form.setFieldValue("files", files);
                  form.clearFieldError("files");
                }}
              />
            </Input.Wrapper>
            <Box className={classes.balanceCard}>
              <Text fz="sm" c="dimmed">
                {receiverBusinessId && !listFailed
                  ? ti("regPayForm.totalRemaining", { count: rows.length })
                  : "-"}
              </Text>
              <Text fz={rem(18)} fw={650} className={classes.num}>
                {receiverBusinessId && !listFailed
                  ? `${currencyFormat(totalRemaining, { withSymbol: false })} ₮`
                  : "-"}
              </Text>
              <Text fz="sm" c="dimmed">
                {ti("regPayForm.overdueRemaining")}{" "}
                {receiverBusinessId && !listFailed ? (
                  <Text span fw={600} c="red.8" className={classes.num}>
                    {currencyFormat(overdueRemaining, { withSymbol: false })} ₮
                  </Text>
                ) : (
                  "-"
                )}
              </Text>
            </Box>
          </Box>
        </Box>

        <Box className={classes.section}>
          <Box className={classes.sectionHead}>
            <Box className={classes.sectionTitle}>
              <Box component="span" className={classes.sectionNum}>
                2
              </Box>
              <Text fw={600} fz="sm">
                {ti("regPayForm.section2")}
              </Text>
              {selectedCount > 0 ? (
                <Badge variant="light" color="gray" radius="xl">
                  {ti("regPayForm.selected", { count: selectedCount })}
                </Badge>
              ) : null}
            </Box>
            {receiverBusinessId && !listFailed ? (
              <Group gap="xs" className={classes.toolbar} justify="flex-end">
                <TextInput
                  size="sm"
                  w={200}
                  placeholder={ti("regPayForm.search")}
                  leftSection={<MagnifyingGlassIcon size={14} />}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.currentTarget.value);
                    setPage(1);
                  }}
                />
                <SegmentedControl
                  size="sm"
                  value={dueTab}
                  onChange={(v) => {
                    setDueTab(v as "all" | "overdue");
                    setPage(1);
                  }}
                  data={[
                    {
                      value: "all",
                      label: ti("regPayForm.tabAll", { count: rows.length }),
                    },
                    {
                      value: "overdue",
                      label: ti("regPayForm.tabOverdue", { count: overdueRows.length }),
                    },
                  ]}
                />
                <Button
                  variant="default"
                  size="sm"
                  disabled={!totalAmountNum || rows.length === 0}
                  onClick={onAutoAllocate}
                >
                  {ti("regPayForm.autoAllocate")}
                </Button>
              </Group>
            ) : null}
          </Box>

          {!receiverBusinessId ? (
            <Text c="dimmed" ta="center" py="xl">
              {ti("regPayForm.selectBuyerFirst")}
            </Text>
          ) : listLoading ? (
            <StackSkeletons />
          ) : listFailed ? (
            <Alert
              color="red"
              title={ti("regPayForm.listLoadFailed")}
              mb="sm"
            >
              <Text fz="sm" mb="sm">
                {(listError as Error)?.message || t("actionFailed")}
              </Text>
              <Button variant="default" size="sm" onClick={() => mutateList()}>
                {ti("regPayForm.retry")}
              </Button>
            </Alert>
          ) : (
            <>
              {count > 500 ? (
                <Alert color="yellow" mb="sm">
                  {ti("regPayForm.limitNotice")}
                </Alert>
              ) : null}
              <AllocationTable
                rows={pageRows}
                filteredLength={filtered.length}
                page={safePage}
                pageSize={PAGE_SIZE}
                allocations={allocations}
                onToggle={onToggle}
                onAmountChange={onAmountChange}
                onPageChange={setPage}
              />
            </>
          )}
        </Box>
      </Box>

      <Box className={classes.footer}>
        <Box className={classes.footerStats}>
          <Group gap="xl" wrap="wrap">
            <Text fz="sm" c="gray.8">
              {ti("regPayForm.received")}{" "}
              <Text span fw={600} c="gray.9" className={classes.num}>
                {currencyFormat(totalAmountNum, { withSymbol: true })}
              </Text>
            </Text>
            <Text fz="sm" c="gray.8">
              {ti("regPayForm.allocated")}{" "}
              <Text span fw={600} c="gray.9" className={classes.num}>
                {currencyFormat(allocated, { withSymbol: true })}
              </Text>
            </Text>
            <Text fz="sm" c="gray.8">
              {ti("regPayForm.diff")}{" "}
              <Text
                span
                fw={600}
                className={classes.num}
                c={diffCents === 0 ? "gray.8" : diffCents > 0 ? "gray.8" : "red.8"}
              >
                {currencyFormat(diff, { withSymbol: true })}
              </Text>
            </Text>
            <Flex align="center" gap={6} c={statusColor}>
              <Box component="span" className={classes.statusDot} />
              <Text fz="sm" fw={500}>
                {statusText}
              </Text>
            </Flex>
          </Group>
          <Progress
            size={6}
            radius="xl"
            value={progressValue}
            color={diffCents < 0 ? "red" : "brand"}
            mt="xs"
          />
        </Box>
        <Group gap="sm" wrap="nowrap">
          <Button variant="default" onClick={() => setAction([])}>
            {t("cancel")}
          </Button>
          <Button
            color="brand"
            leftSection={<FloppyDiskIcon size={16} />}
            loading={loading}
            disabled={!canSubmit}
            onClick={onSubmit}
          >
            {ti("regPayForm.submit")}
          </Button>
        </Group>
      </Box>
    </Box>
  );
}

function StackSkeletons() {
  return (
    <Box>
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} h={36} mb="xs" radius="sm" />
      ))}
    </Box>
  );
}
